import React, { useState, useCallback, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { DrugMapping } from '../lib/drugMappings';
import { DrugSearchService, ExtendedDrugResult } from '../services/drugSearchService';
import { extractDosageFormFromInput, DosageFormMatchResult } from '../services/dosageFormExtractor';
import { useDebouncedCallback } from '../hooks/useDebounce';
import DrugSuggestionsDropdown from './DrugSuggestionsDropdown';
import { API_BASE } from '../lib/api';

interface DrugInputWithSuggestionsProps {
  value: string;
  onChange: (value: string, drug: DrugMapping | ExtendedDrugResult | null) => void;
  onDrugSelect?: (drug: DrugMapping | ExtendedDrugResult) => void;
  onDosageFormDetected?: (result: DosageFormMatchResult) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
}

const DrugInputWithSuggestions: React.FC<DrugInputWithSuggestionsProps> = ({
  value,
  onChange,
  onDrugSelect,
  onDosageFormDetected,
  placeholder = '请输入药品名称（支持中英文，如：奥美拉唑/metformin）',
  className = '',
  disabled = false
}) => {
  const [suggestions, setSuggestions] = useState<ExtendedDrugResult[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [isFocused, setIsFocused] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [searchSources, setSearchSources] = useState<string[]>([]);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });

  const inputRef = useRef<HTMLInputElement>(null);
  const searchServiceRef = useRef<DrugSearchService>(new DrugSearchService(API_BASE));

  const updatePosition = useCallback(() => {
    if (inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY + 4,
        left: rect.left + window.scrollX,
        width: rect.width
      });
    }
  }, []);

  useEffect(() => {
    if (showSuggestions) {
      updatePosition();
      const handleScroll = () => updatePosition();
      const handleResize = () => updatePosition();
      window.addEventListener('scroll', handleScroll, true);
      window.addEventListener('resize', handleResize);
      return () => {
        window.removeEventListener('scroll', handleScroll, true);
        window.removeEventListener('resize', handleResize);
      };
    }
  }, [showSuggestions, updatePosition]);

  const performSearch = useCallback(async (query: string) => {
    if (!query.trim()) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    const dosageMatch = extractDosageFormFromInput(query);
    const searchQuery = dosageMatch ? dosageMatch.drugNameOnly : query;

    if (!searchQuery.trim()) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    setIsLoading(true);
    try {
      const { results, sources } = await searchServiceRef.current.search(searchQuery);
      setSuggestions(results);
      setSearchSources(sources);
      setShowSuggestions(results.length > 0);
    } catch (error) {
      console.error('[DrugSearch] Search error:', error);
      setSuggestions([]);
      setShowSuggestions(false);
    } finally {
      setIsLoading(false);
      setSelectedIndex(-1);
    }
  }, []);

  const debouncedSearch = useDebouncedCallback(performSearch, 200);

  useEffect(() => {
    const searchService = searchServiceRef.current;
    return () => {
      searchService.cancel();
    };
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    onChange(newValue, null);

    if (onDosageFormDetected) {
      const dosageMatch = extractDosageFormFromInput(newValue);
      if (dosageMatch) {
        onDosageFormDetected(dosageMatch);
      }
    }

    debouncedSearch(newValue);
  };

  const handleSuggestionClick = (drug: ExtendedDrugResult) => {
    const displayText = `${drug.nameZh} (${drug.nameEn})`;
    onChange(displayText, drug);
    setSuggestions([]);
    setShowSuggestions(false);
    setSelectedIndex(-1);
    onDrugSelect?.(drug);
  };

  const handleCloseSuggestions = useCallback(() => {
    setShowSuggestions(false);
    setSelectedIndex(-1);
  }, []);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showSuggestions || suggestions.length === 0) {
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev =>
          prev < suggestions.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev =>
          prev > 0 ? prev - 1 : suggestions.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < suggestions.length) {
          handleSuggestionClick(suggestions[selectedIndex]);
        }
        break;
      case 'Escape':
        handleCloseSuggestions();
        break;
    }
  };

  const handleFocus = () => {
    setIsFocused(true);
    if (value.trim()) {
      debouncedSearch(value);
    }
  };

  const handleBlur = () => {
    setIsFocused(false);
  };

  return (
    <div className={className}>
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={value}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={handleFocus}
          onBlur={handleBlur}
          placeholder={placeholder}
          disabled={disabled}
          className="w-full px-4 py-3 pr-10 text-lg border border-gray-300 rounded-lg 
                     focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent
                     dark:bg-gray-800 dark:border-gray-600 dark:text-white
                     dark:placeholder-gray-400 dark:focus:ring-blue-400
                     transition-all duration-200 disabled:opacity-50"
          autoComplete="off"
        />
        <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
          {isLoading ? (
            <svg className="animate-spin h-5 w-5 text-blue-500" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
          ) : (
            <svg
              className={`w-5 h-5 transition-colors duration-200 ${
                isFocused ? 'text-blue-500' : 'text-gray-400'
              }`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
          )}
        </div>
      </div>

      {showSuggestions && createPortal(
        <DrugSuggestionsDropdown
          suggestions={suggestions}
          selectedIndex={selectedIndex}
          isLoading={isLoading}
          searchSources={searchSources}
          query={value}
          position={dropdownPosition}
          onSuggestionClick={handleSuggestionClick}
          onClose={handleCloseSuggestions}
        />,
        document.body
      )}
    </div>
  );
};

export default DrugInputWithSuggestions;
export type { ExtendedDrugResult };
